import React from 'react';
import { gql } from '@apollo/client';
import { TranslatedValue, Translations, useTranslationEdits } from '../common/TranslatedValue';
import { MediaRole, MediaType, PrimaryInfoSection_AdFragment, type UpdateAdInput } from '../../../codegen/graphql';
import Button from '../common/Button';
import { shoeSizeMen_CN_to_FR_mapping } from './LeboncoinAttributesForm';
import SetMarginOrTargetPrice from '../common/SetMarginOrTargetPrice';

export const PRIMARY_INFO_SECTION_FRAGMENT = gql`
  fragment PrimaryInfoSection_ad on Ad {
    id
    title
    title80
    description
    descriptionLeboncoin
    ebayUrl
    cost
    percentageFee
    marginPercentOnBaseCost
    targetListingPriceIncludingVat
    listingPriceRoundingMethod
    quantity
    category
    withVariations
    medias {
      id
      url
      roles
      type
      position
    }
  }
`;

interface PrimaryInfoSectionProps {
  adData: PrimaryInfoSection_AdFragment;
  onUpdate: (input: UpdateAdInput) => void;
}

export function PrimaryInfoSection({ adData, onUpdate }: PrimaryInfoSectionProps) {
  const title = adData.title as unknown as Translations;
  const title80 = (adData.title80 ?? {}) as unknown as Translations;
  const description = (adData.description ?? {}) as unknown as Translations;
  const titleEdits = useTranslationEdits(title);
  const title80Edits = useTranslationEdits(title80);
  const descriptionEdits = useTranslationEdits(description);

  const copyMenShoeSizeTable = () => {
    const content = Object.entries(shoeSizeMen_CN_to_FR_mapping).map(([key, value]) => (
      `Taille FR ${value.shortLabel} = ${value.cms}cm`
    )).join('\n');

    navigator.clipboard.writeText(content);
  }

  const escapeHtml = (s: string) =>
    s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const copyAliexpressDescription = () => {
    const text = (description.FR || '').trim();
    const descriptionImages = (adData.medias || [])
      .filter(
        (m) =>
          m.roles?.includes(MediaRole.Description) && m.type === MediaType.Image
      )
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

    const parts: string[] = [];
    if (text) {
      parts.push(`<p>${escapeHtml(text).replace(/\n/g, '<br/>')}</p>`);
    }
    for (const m of descriptionImages) {
      parts.push(`<p><img src="${escapeHtml(m.url)}" alt="" /></p>`);
    }
    const html = parts.join('\n');
    navigator.clipboard.writeText(html);
  };

  return (
    <>
    <div className="grid grid-cols-2 gap-4">
      <div className="bg-white shadow-sm rounded-lg p-6 space-y-4">
        <TranslatedValue
          translations={titleEdits.value}
          renderAs="input"
          label="Title"
          required
          optimizeTitle
          onEditTranslation={(updates) => onUpdate({ title: titleEdits.edit(updates) })}
        />
        <TranslatedValue
          translations={title80Edits.value}
          renderAs="input"
          label="Title (80 characters)"
          maxLength={80}
          required
          optimizeTitle
          onEditTranslation={(updates) => onUpdate({ title_80: title80Edits.edit(updates) })}
        />

        

        <div className="relative">
          <SetMarginOrTargetPrice
            data={{
              listing: null,
              listingConfiguration: null,
              variation: null,
              ad: adData,
            }}
            setForKey={`ad-${adData.id}`}
            onUpdate={(data) => onUpdate(data)}
          />
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-lg p-6 space-y-4">
        <TranslatedValue
          translations={descriptionEdits.value}
          renderAs="input"
          label="Description"
          multiline
          rows={20}
          onEditTranslation={(updates) => onUpdate({ description: descriptionEdits.edit(updates) })}
        />
      </div>
    </div>
    </>
  );
}
